import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import AppLayout from './layouts/AppLayout.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import { DEFAULT_MENU_ITEM } from './config/menuData.js';
import './App.scss';

// Pages are generated per the design reference (ui-components-from-reference)
// and their slug routes are registered inside the <AppLayout> group below.
// Until a page's route exists, its slug falls through to the 404 catch-all.
const LoginPage = lazy(() => import('./pages/login/index.jsx'));
const OperationsConsole = lazy(() => import('./pages/operations-console/index.jsx'));
const NominationIntakeConsole = lazy(() => import('./pages/nomination-intake-console/index.jsx'));
const CreditComplianceBoard = lazy(() => import('./pages/credit-compliance-board/index.jsx'));
const OrganizationEntitiesSettings = lazy(() => import('./pages/organization-entities-settings/index.jsx'));
const FleetAssetsSettings = lazy(() => import('./pages/fleet-assets-settings/index.jsx'));
const SchedulingGanttBoard = lazy(() => import('./pages/scheduling-gantt-board/index.jsx'));
const BargeMasterExecutionScreen = lazy(() => import('./pages/barge-master-execution-screen/index.jsx'));
const InvoicingErpConsole = lazy(() => import('./pages/invoicing-erp-console/index.jsx'));
const ComputationsQualityAuditor = lazy(() => import('./pages/computations-quality-auditor/index.jsx'));
const ClaimsDisputesManager = lazy(() => import('./pages/claims-disputes-manager/index.jsx'));
const RulesPacksAdministration = lazy(() => import('./pages/rules-packs-administration/index.jsx'));
const IntegrationsSettings = lazy(() => import('./pages/integrations-settings/index.jsx'));
const OperationalParametersRegistry = lazy(() => import('./pages/operational-parameters-registry/index.jsx'));
const NotificationsTemplatesSettings = lazy(() => import('./pages/notifications-templates-settings/index.jsx'));
const BuyerPortalAdministration = lazy(() => import('./pages/buyer-portal-administration/index.jsx'));
const BuyerPortal = lazy(() => import('./pages/buyer-portal/index.jsx'));
const UsersRolesApprovalsSettings = lazy(() => import('./pages/users-roles-approvals-settings/index.jsx'));

const NotFound404 = lazy(() => import('./pages/status/NotFound404.jsx'));
const Unauthorized401 = lazy(() => import('./pages/status/Unauthorized401.jsx'));
const Forbidden403 = lazy(() => import('./pages/status/Forbidden403.jsx'));
const ServerError500 = lazy(() => import('./pages/status/ServerError500.jsx'));

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public, full-screen authentication page (no app shell). */}
            <Route path="/login" element={<LoginPage />} />

            <Route element={<AppLayout />}>
              {/* Authenticated app pages. */}
              <Route element={<ProtectedRoute />}>
                {/* Landing page — the design's default menu item / dashboard. */}
                <Route index element={<Navigate to={`/${DEFAULT_MENU_ITEM.slug}`} replace />} />

                {/* Customer surface — external buyers only. */}
                <Route element={<RoleRoute allow={['Buyer']} />}>
                  <Route path="buyer-portal" element={<BuyerPortal />} />
                </Route>

                {/* Internal operations shell — walled off from external buyers. */}
                <Route element={<RoleRoute deny={['Buyer']} />}>
                  <Route path="operations-console" element={<OperationsConsole />} />
                  <Route path="nomination-intake-console" element={<NominationIntakeConsole />} />
                  <Route path="credit-compliance-board" element={<CreditComplianceBoard />} />
                  <Route path="organization-entities-settings" element={<OrganizationEntitiesSettings />} />
                  <Route path="fleet-assets-settings" element={<FleetAssetsSettings />} />
                  <Route path="scheduling-gantt-board" element={<SchedulingGanttBoard />} />
                  <Route path="barge-master-execution-screen" element={<BargeMasterExecutionScreen />} />
                  <Route path="invoicing-erp-console" element={<InvoicingErpConsole />} />
                  <Route path="computations-quality-auditor" element={<ComputationsQualityAuditor />} />
                  <Route path="claims-disputes-manager" element={<ClaimsDisputesManager />} />
                  <Route path="rules-packs-administration" element={<RulesPacksAdministration />} />
                  <Route path="integrations-settings" element={<IntegrationsSettings />} />
                  <Route path="operational-parameters-registry" element={<OperationalParametersRegistry />} />
                  <Route path="notifications-templates-settings" element={<NotificationsTemplatesSettings />} />
                  <Route path="buyer-portal-administration" element={<BuyerPortalAdministration />} />
                  <Route path="users-roles-approvals-settings" element={<UsersRolesApprovalsSettings />} />
                </Route>
              </Route>

              {/* Generic status pages. */}
              <Route path="401" element={<Unauthorized401 />} />
              <Route path="403" element={<Forbidden403 />} />
              <Route path="500" element={<ServerError500 />} />

              {/* Catch-all — any unresolved / not-yet-built route lands here. */}
              <Route path="*" element={<NotFound404 />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
